import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import React, { useState } from 'react'
import { mediaQueryUp } from 'components/utils/breakpoint'
import rem from 'polished/lib/helpers/rem'
import styled from 'styled-components'
import Label from 'LuxKit/Label'
import TextLink from 'LuxKit/TextLink'
import ReserveForZeroPolicyModalContent from './ReserveForZeroPolicyModalContent'
import switchFunc from 'lib/function/switchFunc'
import { depositAmountStringBuilder } from 'lib/tours/tourUtils'
import DepositInfoModalContent from '../DepositInfo/DepositInfoModalContent'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { isOfferDefaultDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { useAppSelector } from 'hooks/reduxHooks'

enum ModalSteps {
  deposit = 'deposit',
  reserve = 'reserve'
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const Section = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(20)};

  &:nth-child(2) {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    width: 50%;

    &:nth-child(2) {
      margin-top: 0;
      margin-left: ${rem(20)};
    }
  }
`

const SectionSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(20)};
`

const TopSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
`

const BottomSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  margin-top: ${rem(16)};
`

interface Props {
  offerType: App.OfferType;
  depositAmountPercentage: number;
  depositType?: App.Tours.V2OfferDepositType
  depositThresholds?: App.DepositThreshold
  depositAmount?: number;
}

function ReserveForZeroAndDepositPolicyModal({
  depositAmountPercentage,
  depositType,
  depositThresholds,
  offerType,
  depositAmount,
}: Props) {
  const [step, setStep] = useState<ModalSteps>()

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositTitleString = isOfferUsingDefaultDepositPercentage ?
    'a deposit' : !depositType ? `${depositAmountPercentage}% deposit` : `${depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage).short} deposit`

  const depositAmountString = isOfferUsingDefaultDepositPercentage ?
    'deposit' : !depositType ? `${depositAmountPercentage}% deposit` : `${depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage).short} deposit`

  const title = switchFunc({
    deposit: `Pay ${depositTitleString}`,
    reserve: 'Reserve now for free',
  }, 'Flexible payment options')(step)

  const subtitle = switchFunc({
    deposit: undefined,
    reserve: undefined,
  }, 'Book with ease by choosing one of our flexible payment methods below.')(step as string)

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      size="L"
      onBack={step ? () => setStep(undefined) : undefined}
    >
      {step === ModalSteps.reserve && (
        <ReserveForZeroPolicyModalContent />
      )}
      {step === ModalSteps.deposit && (
        <DepositInfoModalContent
          offerType={offerType}
          depositAmountPercentage={depositAmountPercentage}
          depositThresholds={depositThresholds}
          depositAmount={depositAmount}
        />
      )}
      {!step && (
        <SectionContainer>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                Reserve now for free
              </Heading>
              <BodyText variant="medium">We'll hold your reservation for 48 hours so you have time to think it over.</BodyText>
              <div>
                <Label kind="tertiary" variant="warning">Limit of 2 reservations per member</Label>
              </div>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.reserve)} size="large">Learn more</TextLink>
            </BottomSection>
          </Section>
          <SectionSeparator>
            <Heading variant="heading6">Or</Heading>
          </SectionSeparator>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                Pay {depositTitleString} or in full today
              </Heading>
              <BodyText variant="medium">Secure your dates by paying the full amount upfront or with a {depositAmountString} of your accommodation booking.</BodyText>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.deposit)} size="large">Learn more</TextLink>
            </BottomSection>
          </Section>
        </SectionContainer>
      )}
    </Modal>
  )
}

export default ReserveForZeroAndDepositPolicyModal
